import { useState } from "react";
import {
  Search,
  Download,
  Eye,
  ShoppingCart,
  ChevronDown,
  Loader,
} from "lucide-react";
import { exportTableData } from "../../utils/exportTable";
import { fetchOrders } from "../../services/orderService";
import { useNavigate } from "react-router-dom";
import { useOrders } from "../../hooks/useOrders";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import Pagination from "../../components/Pagination";
import formatCurrency from "../../utils/formatCurrency";

const Orders = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const debouncedSearch = useDebouncedValue(search, 700);
  const normalizedStatus = [
    "pending_payment",
    "pending_acceptance",
    "processing",
    "shipped",
    "delivered",
    "cancelled",
  ].includes(statusFilter)
    ? statusFilter
    : statusFilter === "all"
    ? ""
    : statusFilter;

  const { orders, loading, pagination, orderStats } = useOrders(
    currentPage,
    debouncedSearch,
    normalizedStatus,
  );

  const stats = [
    { label: "Total Orders", value: orderStats?.total_orders ?? 0 },
    { label: "Items Sold", value: orderStats?.items_sold ?? 0 },
    { label: "Completed Orders", value: orderStats?.completed_orders ?? 0 },
    { label: "Pending Orders", value: orderStats?.pending_orders ?? 0 },
    {
      label: "Total Revenue",
      value: formatCurrency(orderStats?.total_revenue),
    },
  ];

  const getStatColor = (label) => {
    switch (label) {
      case "Completed Orders":
        return "text-green-600";
      case "Pending Orders":
        return "text-orange-500";
      default:
        return "text-slate-900";
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-50 text-green-700 border-green-100";
      case "Shipped":
        return "bg-blue-50 text-blue-600 border-blue-100";
      case "out_for_delivery":
        return "bg-slate-50 text-slate-900 border-slate-200";
      case "Processing":
        return "bg-orange-50 text-orange-600 border-orange-100";
      case "Cancelled":
        return "bg-red-50 text-red-600 border-red-100";
      case "pending_payment":
        return "bg-yellow-50 text-yellow-600 border-yellow-100";
      default:
        return "bg-slate-50 text-slate-600";
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case "Delivered":
        return "bg-green-600";
      case "Shipped":
        return "bg-blue-600";
      case "out_for_delivery":
        return "bg-slate-900";
      case "Processing":
        return "bg-orange-500";
      case "Cancelled":
        return "bg-red-600";
      case "pending_payment":
        return "bg-yellow-600";
      default:
        return "bg-slate-400";
    }
  };

  // --- Export Orders Handler ---
  const handleExportOrders = async () => {
    if (!pagination?.last_page) return;
    setExporting(true);
    try {
      const fetchPageData = async (page) => {
        const data = await fetchOrders(page, debouncedSearch);
        return data?.message?.orders?.data || [];
      };
      const columns = [
        { key: "id", header: "Order ID" },
        { key: "customer", header: "Customer" },
        { key: "created_at", header: "Date" },
        { key: "order_items_count", header: "Items" },
        { key: "total", header: "Total" },
        { key: "order_status", header: "Status" },
      ];
      const mapRow = (order) => ({
        id: `ORD-${order.id}`,
        customer: order.user ? `${order.user?.name || "Unknown"} (${order.user?.email || "N/A"})` : "",
        created_at: order.created_at ? order.created_at.slice(0, 10) : "",
        order_items_count: order.order_items_count || 0,
        total: order.payment_transaction
          ? order.payment_transaction?.amount || 0
          : "",
        order_status: order.order_status,
      });
      const allRows = [];
      for (let page = 1; page <= pagination.last_page; page++) {
        // eslint-disable-next-line no-await-in-loop
        const pageData = await fetchPageData(page);
        allRows.push(...pageData.map(mapRow));
      }
      exportTableData(async () => allRows, 1, columns, "orders_export.csv");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 text-slate-600">
      {/* --- HEADER --- */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-8 gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
            Orders
          </h1>
          <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
            Manage customer orders and track shipments
          </p>
        </div>
        <button
          className="flex items-center gap-2 bg-[#D2BDA1] text-black text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-[#c5b49f] transition-colors active:scale-95 active:shadow-sm duration-100 w-full sm:w-auto justify-center disabled:opacity-60"
          onClick={handleExportOrders}
          disabled={loading || !pagination?.last_page || exporting}
        >
          {exporting ? (
            <>
              <Loader size={16} className="animate-spin" /> Exporting...
            </>
          ) : (
            <>
              <Download size={18} /> Export Orders
            </>
          )}
        </button>
      </div>

      {/* --- STATS CARDS --- */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-0 mb-6 sm:mb-10 border-y border-[#EEF1F3]">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`p-4 sm:p-6 ${index !== stats.length - 1 ? "border-r border-[#EEF1F3] first:border-l-0 last:border-r-0" : ""}`}
          >
            <p className="text-[#7D828D] font-medium text-xs sm:text-sm mb-1 sm:mb-2">
              {stat.label}
            </p>
            <p
              className={`text-lg sm:text-2xl font-medium ${getStatColor(stat.label)}`}
            >
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <h2 className="text-lg sm:text-xl font-medium mb-2 sm:mb-4 text-[#0C0C0E]">
        Order List
      </h2>

      {/* --- TOOLBAR --- */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="relative flex-1 max-w-full sm:max-w-sm">
          <Search
            className="text-xs sm:text-sm absolute left-3 top-1/2 -translate-y-1/2 text-[#7D828D]"
            size={18}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search by customer name or email..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] text-xs sm:text-sm"
          />
        </div>
        <div className="relative w-full sm:w-auto">
          <select
            className="appearance-none bg-[#FBF8F6] border border-[#F1F2F3] pl-4 pr-10 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] min-w-[120px] sm:min-w-[140px] font-medium text-slate-700 text-xs sm:text-sm w-full sm:w-auto"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Status</option>
            <option value="pending_payment">Pending Payment</option>
            <option value="pending_acceptance">Pending Acceptance</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            size={18}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        /* --- EMPTY STATE VIEW --- */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            <ShoppingCart size={32} className="text-[#BFAC93]" />
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No orders yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            This page will update automatically when new orders are received.
          </p>
        </div>
      ) : (
        <>
          {/* --- TABLE --- */}
          <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
            <table className="min-w-[700px] w-full text-left shadow-sm text-xs sm:text-sm">
              <thead className="bg-[#FBF8F6] text-[#7D828D] font-medium">
                <tr>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Order ID</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Customer</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Date</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Items</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Total</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Status</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                      ORD-{order.id}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-[#0C0C0E]">
                          {order.user?.name || "Unknown"}
                        </span>
                        <span className="text-xs text-[#7D828D]">
                          {order.user?.email || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                      {order.created_at ? order.created_at.slice(0, 10) : ""}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-[#7D828D]">
                      {order.order_items_count || 0}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                      {formatCurrency(order.payment_transaction?.amount || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusStyles(order.status)}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${getStatusDot(order.order_status)}`}
                        ></span>
                        {(() => {
                          switch (order.order_status) {
                            case "Delivered":
                              return "Delivered";
                            case "Shipped":
                              return "Shipped";
                            case "out_for_delivery":
                              return "Delivering";
                            case "Processing":
                              return "Processing";
                            case "Cancelled":
                              return "Cancelled";
                            case "pending_payment":
                              return "Pending Payment";
                            default:
                              return order.order_status;
                          }
                        })()}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <button
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="flex items-center gap-1 text-[#728550] hover:text-slate-900 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
                      >
                        <Eye size={16} /> View Order
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* --- PAGINATION --- */}
          {pagination && (
            <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full overflow-x-auto min-w-0">
              <span className="text-xs sm:text-sm text-[#7D828D]">
                Showing{" "}
                {pagination?.current_page
                  ? (pagination.current_page - 1) * pagination.per_page + 1
                  : 0}{" "}
                to{" "}
                {Math.min(
                  pagination?.current_page * pagination?.per_page,
                  pagination?.total,
                )}{" "}
                of {pagination?.total} products
              </span>
              <div className="w-full sm:w-auto">
                <Pagination
                  currentPage={pagination?.current_page}
                  totalPages={pagination?.last_page}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Orders;
